import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import type { GetMaintenanceResponse, PendingToggle } from "./schema/maintenance.schema";
import { fetchFlagsHandler, patchFlagHandler } from "./schema/handler-functions";
import { useNavigate } from "react-router-dom";
import { useRoles } from "@/hooks/useRoles";
import { Box, Flex, useDisclosure } from "@chakra-ui/react";
import {  useForm } from "react-hook-form";
import { MaintenanceSwitchRow } from "@/components/common/section-components/maintenance/MaintenanceSwitchRow";
import { getSectionLabel, SectionMap } from "./schema/section-labels";
import { MaintenanceModuleCard } from "@/components/common/section-components/maintenance/MaintenanceModuleCard";

/**
 * Página de mantenimiento.
 * @returns JSX de la página.
 */
export const MaintenancePage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  const [flags, setFlags] = useState<GetMaintenanceResponse | null>(null);
  const [loadingFlags, setLoadingFlags] = useState<boolean>(false);
  const [loadingPatchFlag, setLoadingPatchFlag] = useState<boolean>(false);
  const [loadingPatchFieldName, setLoadingPatchFieldName] = useState<string | null>(null);

  const { control, reset, setValue } = useForm<Record<string, boolean>>({
    defaultValues: {},
  });

  const { open: openConfirmAction, setOpen: setOpenConfirmAction } = useDisclosure();
  const [pendingToggle, setPendingToggle] = useState<PendingToggle | null>(null);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const navigate = useNavigate();

  const { fetchPermissions } = useRoles();

  const moduleEntries = useMemo(() => {
    if (!flags?.modules) return [] as Array<[string, { sections: SectionMap }]>;
    const moduleOrder = [
      "FINANCIAL",
      "GRANARY",
      "DAILY_MARKET",
      "OTHER",
      "SUPPLIES",
      "ONBOARDING"
    ];

    const getOrderIndex = (moduleCode: string) => {
      const idx = moduleOrder.indexOf(moduleCode);
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };

    return Object.entries(flags.modules).sort(([a], [b]) => {
      const aIdx = getOrderIndex(a);
      const bIdx = getOrderIndex(b);

      if (aIdx !== bIdx) return aIdx - bIdx;
      return a.localeCompare(b);
    });
  }, [flags?.modules]);

  const isGeneralAppGateOn = useMemo(() => {
    if (!flags) return false;
    return flags.general === false;
  }, [flags]);

  /**
   * Obtiene el estado actual de mantenimiento desde el backend.
   * También aplica el manejo de permisos/errores mediante el handler compartido.
   */
  const fetchMaintenance = useCallback(async () => {
    await fetchFlagsHandler({
      setLoadingFlags,
      setFlags,
      navigate,
      fetchPermissions,
    });
  }, [fetchPermissions, navigate]);

  useEffect(() => {
    if (!flags) return;

    const defaultValues: Record<string, boolean> = {
      "GENERAL:GENERAL_APP": !flags.general,
    };

    Object.entries(flags.modules ?? {}).forEach(([moduleCode, moduleData]) => {
      Object.entries(moduleData.sections ?? {}).forEach(([sectionCode, flagEnabled]) => {
        defaultValues[`${moduleCode}:${sectionCode}`] = !flagEnabled;
      });
    });

    reset(defaultValues);
  }, [flags, reset]);

  /**
   * Guarda la acción pendiente y abre el modal.
   * Se usa para no ejecutar el PATCH inmediatamente al clickear un switch.
   */
  const requestConfirm = useCallback(
    (args: PendingToggle) => {
      setPendingToggle(args);
      setOpenConfirmAction(true);
    },
    [setOpenConfirmAction],
  );

  /**
   * Confirma la acción del modal.
   * Importante: la UI representa "Activar mantenimiento" => backend requiere desactivar flag.
   * Por eso: nuevoEstado = !nextValue.
   */
  const handleAcceptToggle = useCallback(async () => {
    if (!pendingToggle) return;

    setLoadingPatchFieldName(pendingToggle.fieldName);

    try {
      await patchFlagHandler({
        setLoadingPatchFlag,
        navigate,
        fetchPermissions,
        fetchFlags: fetchMaintenance,
        values: {
          codigoModulo: pendingToggle.codigoModulo,
          codigoSeccion: pendingToggle.codigoSeccion,
          nuevoEstado: !pendingToggle.nextValue,
        },
      });
    } finally {
      setLoadingPatchFieldName(null);
    }

    setOpenConfirmAction(false);
    setPendingToggle(null);
  }, [fetchMaintenance, fetchPermissions, navigate, pendingToggle, setOpenConfirmAction]);

  /**
   * Si el modal se cierra sin aceptar (Cancelar o X), se hace rollback del switch.
   * Esto es necesario porque el ConfirmModal no expone un callback de cancel.
   */
  useEffect(() => {
    if (openConfirmAction) return;
    if (!pendingToggle) return;

    setValue(pendingToggle.fieldName, pendingToggle.prevValue, {
      shouldDirty: false,
      shouldTouch: false,
    });
    setPendingToggle(null);
  }, [openConfirmAction, pendingToggle, setValue]);

  useEffect(() => {
    if (isActive) fetchMaintenance();
  }, [isActive]);

  return (
    <Flex
      flexDir="column"
      paddingY="20px"
      gap="24px"
      height="100%"
      minH="0"
      data-testid="maintenance-page"
    >
      <SectionHeader
        title="Gestión Mantenimiento Mobile"
        loading={loadingFlags}
        showButtons={false}
      />

      <Suspense>
        <ConfirmModalDynamic
          openConfirm={openConfirmAction}
          setOpenConfirm={setOpenConfirmAction}
          handleAccept={handleAcceptToggle}
          loadingAccept={loadingPatchFlag}
          title="Confirmación"
          subtitle="¿Deseas confirmar esta acción?"
        />
      </Suspense>

      <Flex flexDir="column" flex="1" minH="0" overflowY="auto" gap="16px">
        {flags && (
          <Flex flexDir="column" gap="16px">
            <Box border="1px solid" borderColor="gray.200" borderRadius="8px" padding="12px">
              <MaintenanceSwitchRow
                control={control}
                fieldName="GENERAL:GENERAL_APP"
                label={getSectionLabel("GENERAL_APP")}
                disabled={loadingPatchFlag}
                showSpinner={loadingPatchFieldName === "GENERAL:GENERAL_APP"}
                onRequestConfirm={requestConfirm}
                codigoModulo="GENERAL"
                codigoSeccion="GENERAL_APP"
              />
            </Box>

            {moduleEntries.map(([moduleCode, moduleData]) => (
              <MaintenanceModuleCard
                key={moduleCode}
                moduleCode={moduleCode}
                sections={moduleData.sections}
                control={control}
                disabled={loadingPatchFlag || isGeneralAppGateOn}
                moduleGateOn={moduleData.sections?.GENERAL === false}
                loadingFieldName={loadingPatchFieldName}
                onRequestConfirm={requestConfirm}
              />
            ))}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

/**
 * Modal de confirmación dinámico.
 */
const ConfirmModalDynamic = lazy(() =>
  import("@/components/modal/ConfirmModal").then((el) => ({
    default: el["ConfirmModal"],
  })),
);